<template>
  <!-- previewConfig の CSS を <head> に注入 (@media 対応) -->
  <!-- <style> タグはテンプレート内で直接書けないため component :is で代替 -->
  <Teleport to="head">
    <component :is="'style'" v-if="previewDivCss"    id="preview-div-css">{{ previewDivCss }}</component>
    <component :is="'style'" v-if="previewShadowCss" id="preview-shadow-css">{{ previewShadowCss }}</component>
    <component :is="'style'" v-if="previewImgCss"    id="preview-img-css">{{ previewImgCss }}</component>
  </Teleport>

  <div v-if="uiStore.activePanel === 'preview'" id="preview_wrapper">
    <h2>オリジナルラベル完成イメージ</h2>

    <div
      id="gallery"
      :style="galleryStyle"
    >
      <div class="shadow" :style="shadowStyle" />
      <img class="preview" ref="previewImgRef" v-if="orderStore.previewDataUrl" :src="orderStore.previewDataUrl" :style="previewImgStyle" />
    </div>

    <ul style="z-index:5;">
      <!-- やり直す -->
      <li class="back_deco">
        <a href="#" @click.prevent="remake">
          <img src="/img/btBack.png"><span>やり直す</span>
        </a>
      </li>

      <!-- NextEngine -->
      <template v-if="context.isNep">
        <li>
          <a href="#" @click.prevent="submitOrder">
            <img src="/img/btSelect.png"><span>画像を送信する<br>(やり直しはできません)</span>
          </a>
        </li>
      </template>

      <!-- 通常 / DrinkSuit -->
      <template v-else-if="!context.isMall">
        <template v-if="context.isDrinkSuit">
          <li>
            <a href="#" @click.prevent="submitOrder">
              <img src="/img/btCart.png">
              <span>ショッピングカートにすすむ<br>(やり直しはできません)</span>
            </a>
          </li>
        </template>
        <template v-else>
          <li>
            <a href="/item">
              <img src="/img/btSelect.png"><span>実際に作ってみる<br>(まずは、商品の選択へ)</span>
            </a>
          </li>
        </template>
      </template>

      <!-- モール -->
      <template v-else>
        <li>
          <a href="#" @click.prevent="submitOrder">
            <img src="/img/btSelect.png"><span>送信する<br>(やり直しはできません)</span>
          </a>
        </li>
      </template>
    </ul>

    <!-- 送信フォーム (hidden) -->
    <form ref="formRef" method="POST" :action="orderStore.formAction">
      <input type="hidden" name="_token" :value="orderStore.formData._token" />
      <input type="hidden" name="session_id" :value="sessionId" />
      <input type="hidden" name="client_id" :value="clientId" />
      <input v-if="orderStore.formData.drink_suit" type="hidden" name="drink_suit" :value="orderStore.formData.drink_suit" />
      <input v-if="orderStore.formData.next_engine_picture_id" type="hidden" name="next_engine_picture_id" :value="orderStore.formData.next_engine_picture_id" />
      <input v-if="orderStore.formData.next_engine_denpyo_id" type="hidden" name="next_engine_denpyo_id" :value="orderStore.formData.next_engine_denpyo_id" />
      <input type="hidden" name="deco_list" :value="decoListJson" />
      <input type="hidden" name="acceptImage" :value="orderStore.previewDataUrl" />
      <input type="submit" style="visibility:hidden" />
    </form>
  </div>
</template>

<script setup>
import { ref, computed, watch, inject, onMounted, nextTick } from 'vue'
import { useOrderStore } from '../../stores/useOrderStore'
import { useUiStore } from '../../stores/useUiStore'
import { useCanvasStore } from '../../stores/useCanvasStore'
import { exportLabelImage } from '../../composables/useExport'

const orderStore = useOrderStore()
const uiStore = useUiStore()
const canvasStore = useCanvasStore()
const formRef = ref(null)
const previewImgRef = ref(null)
const sessionId = ref('')
const clientId = ref('')
// 旧システムの img_style 計算結果 (aspect_ratio → 幅・マージン調整)
// 旧: $('#gallery').children().attr("style", img_style) で .shadow と .preview 両方に適用
const aspectStyle = ref({})

onMounted(() => {
  // GA IDs are populated by inline script in blade after 3s
  setTimeout(() => {
    sessionId.value = window.__ga_session_id__ || ''
    clientId.value = window.__ga_client_id__ || ''
  }, 3500)
})

// DecoCanvas の stageRef を親から inject (App.vue でprovide)
const stageRef = inject('stageRef', null)

const context = computed(() => orderStore.context)
const decoListJson = computed(() => JSON.stringify(orderStore.decoCountAsArray()))

// --- CSS を <style> タグ用文字列に変換 ---
// @media ブロックはそのまま出力する。
// @media 以外の部分:
//   { を含む場合 → セレクター付きCSS として そのまま出力
//   { を含まない場合 → 宣言のみとみなし selector でラップ
function buildCssForStyleTag(raw, selector) {
  const mediaBlocks = []
  let base = ''
  let i = 0
  while (i < raw.length) {
    if (raw.slice(i, i + 6).toLowerCase() === '@media') {
      let depth = 0, start = i
      while (i < raw.length) {
        if (raw[i] === '{') depth++
        else if (raw[i] === '}') { depth--; if (depth === 0) { i++; break } }
        i++
      }
      mediaBlocks.push(raw.slice(start, i))
    } else {
      base += raw[i++]
    }
  }
  let result = ''
  const trimmed = base.trim()
  if (trimmed) {
    // セレクター付きCSSが入力されている場合はそのまま出力
    if (trimmed.includes('{')) {
      result += trimmed + '\n'
    } else {
      result += `${selector} {\n${trimmed}\n}\n`
    }
  }
  if (mediaBlocks.length) result += mediaBlocks.join('\n')
  return result
}

// Teleport で <head> に注入する CSS 文字列
const previewDivCss = computed(() => {
  const css = orderStore.previewConfig.divCss
  return css ? buildCssForStyleTag(css, '#preview_wrapper #gallery') : ''
})
const previewShadowCss = computed(() => {
  const css = orderStore.previewConfig.shadowCss
  return css ? buildCssForStyleTag(css, '#preview_wrapper #gallery .shadow') : ''
})
const previewImgCss = computed(() => {
  const css = orderStore.previewConfig.imgCss
  return css ? buildCssForStyleTag(css, '#preview_wrapper #gallery img.preview') : ''
})

const galleryStyle = computed(() => {
  const cfg = orderStore.previewConfig
  return cfg.imgUrl ? { backgroundImage: `url('${cfg.imgUrl}')` } : {}
})

// 旧システム: img_style を .shadow にも適用
const shadowStyle = computed(() => ({ ...aspectStyle.value }))

// 旧システム再現:
// aspect_ratio に基づき幅・マージンを計算して inline style で上書き
// + background: rgba(0,0,0,0) を付与
// imgCss は <style> タグで適用済みのため inline style には含めない
const previewImgStyle = computed(() => ({
  ...(!orderStore.previewConfig.imgCss ? aspectStyle.value : {}),
  background: 'rgba(0,0,0,0)',
}))

// 旧システムの img_style 計算:
// aspect_ratio に基づき .preview の offsetHeight から幅・マージンを決定
// 旧: if (aspect_ratio > 1) { height = h/r } else { width = h*r; margin-left = -w/2 }
async function computeAspectStyle() {
  await nextTick()
  const imgEl = previewImgRef.value
  if (!imgEl) return
  const r = canvasStore.aspectRatio
  if (!r || r === 1.0) return  // 正方形は調整不要
  const h = imgEl.offsetHeight  // プロジェクトCSSで定義された高さ
  if (!h) return
  const style = {}
  if (r > 1) {
    style.height = Math.round(h / r) + 'px'
  } else {
    const w = Math.round(h * r)
    style.width = w + 'px'// margin-left はCSSの left:0;right:0;margin:auto でセンタリングするため設定しない
  }
  aspectStyle.value = style
}

// プレビューパネルが開いたらエクスポート実行
watch(() => uiStore.activePanel, async (panel) => {
  if (panel !== 'preview') return
  aspectStyle.value = {}
  uiStore.setLoading(true)
  try {
    const dataUrl = await exportLabelImage(stageRef)
    orderStore.setPreviewDataUrl(dataUrl)
    await computeAspectStyle()
  } finally {
    uiStore.setLoading(false)
  }
})

function remake() {
  orderStore.setPreviewDataUrl('')
  uiStore.hidePanel()
}

function submitOrder() {
  uiStore.isSubmitting = true
  formRef.value?.submit()
}
</script>
